/**
 * React Query hooks for the Feedback admin screen. Same pattern as
 * useEscalations: mutations invalidate list + stats so the queue
 * stays live after status updates.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FeedbackListParams,
  FeedbackUpdateInput,
  fetchFeedback,
  fetchFeedbackOne,
  fetchFeedbackStats,
  updateFeedback
} from '@/api/feedback';

export function useFeedback(params: FeedbackListParams) {
  return useQuery({
    queryKey: ['feedback', params],
    queryFn: () => fetchFeedback(params),
    refetchInterval: 60_000,
    placeholderData: prev => prev
  });
}

export function useFeedbackStats() {
  return useQuery({
    queryKey: ['feedbackStats'],
    queryFn: fetchFeedbackStats,
    refetchInterval: 60_000
  });
}

export function useFeedbackOne(id: string | null) {
  return useQuery({
    queryKey: ['feedback', 'one', id],
    queryFn: () => fetchFeedbackOne(id!),
    enabled: !!id
  });
}

export function useUpdateFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FeedbackUpdateInput) => updateFeedback(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feedback'] });
      qc.invalidateQueries({ queryKey: ['feedbackStats'] });
    }
  });
}
